import { default as MenuIcon } from '@mui/icons-material/Menu';
import { Fade, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import { getColorVariants } from '@/components/GlobalNavigation/DesktopNav/DesktopNav';
import Dropdown from '@/components/GlobalNavigation/Dropdown/Dropdown';
import {
  type GlobalNavigationProps,
  getPosition,
  NavPosition,
} from '@/components/GlobalNavigation/GlobalNavigation';
import useHelperNav from '@/components/GlobalNavigation/hooks/useHelperNav';
import useHideNav from '@/components/GlobalNavigation/hooks/useHideNav';
import useNavPosition from '@/components/GlobalNavigation/hooks/useNavPosition';
import useNavTransparency from '@/components/GlobalNavigation/hooks/useNavTransparency';
import LocationDisplay from '@/components/GlobalNavigation/Location/LocationDisplay/LocationDisplay';
import NavBanner from '@/components/GlobalNavigation/NavBanner/NavBanner';
import useSharedStyles from '@/components/GlobalNavigation/SharedNav.styles';
import Icon, { IconName } from '@/components/Icon/Icon';
import Link from '@/components/Link/Link';
import { getLoginUrlByEnv } from '@/constants/baseUrls';
import { PHONE } from '@/constants/info';
import type { FieldlessDropdownMenu } from '@/contentful/types';
import type { userLocationInterface } from '@/hooks/geo';
import TextLogo from '@/public/cmsAssets/assets/img/logo/TextLogo';
import { brandColors } from '@/styles/sunrunTheme.styles';
import { trackEvents } from '@/utils/analytics';
import useScrollDirection from '@/utils/useScrollDirection';
import useScrollPosition from '@/utils/useScrollPosition';

import useStyles from './MobileNav.styles';

interface MobileNavProps extends GlobalNavigationProps, userLocationInterface {
  isOnDark: boolean;
}

const MobileNav = ({
  categories,
  promoCta,
  loginCta,
  primaryCta,
  secondaryCta,
  userLocation,
  setUserLocation,
  immersive,
  isOnDark,
}: MobileNavProps) => {
  const [selectedMobileNavItem, setSelectedMobileNavItem] =
    useState<FieldlessDropdownMenu | null>(null);

  const [transparentNavDisabled, setTransparentNavDisabled] =
    useState<boolean>(false);

  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const [mobileNavLinksView, setMobileNavLinksView] = useState<boolean>(false);
  const [locationOpen, setLocationOpen] = useState<boolean>(false);

  const scrollPosition = useScrollPosition();
  const scrollDirection = useScrollDirection();
  const { typography } = useTheme();

  const hideNav = useHideNav(scrollPosition, scrollDirection);
  const [isHelperNav] = useHelperNav(scrollPosition, scrollDirection);
  const [transparentNav] = useNavTransparency(
    immersive,
    scrollPosition,
    scrollDirection
  );
  const [navPosition, setNavPosition] = useNavPosition(
    immersive,
    scrollPosition,
    scrollDirection
  );

  const mobileDropdownCloseDebounce = () => {
    setMobileNavLinksView(false);
    setTimeout(() => {
      setSelectedMobileNavItem(null);
      setLocationOpen(false);
    }, 300);
  };

  // when in immersive and the dropdown is open then it should be solid and positioning is fixed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    if (immersive) {
      if (mobileNavOpen) {
        setNavPosition(NavPosition.FIXED);
        setTransparentNavDisabled(true);
      } else {
        setNavPosition(NavPosition.ABSOLUTE);
        setTransparentNavDisabled(false);
      }
    }
  }, [mobileNavOpen]);

  const router = useRouter();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    setMobileNavLinksView(false);
    setMobileNavOpen(false);
    setSelectedMobileNavItem(null);
  }, [router?.asPath]);

  // prevent body scrolling if nav is open
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? 'hidden' : 'unset';
  }, [mobileNavOpen]);

  const useDarkStyles = isOnDark && !isHelperNav;
  const { classes } = useStyles({ isOnDark: useDarkStyles });
  const { classes: sharedStyles, cx } = useSharedStyles({
    useTransition: true,
  });

  const isNavTransparent = transparentNav && !transparentNavDisabled;

  const { logoClass, navContainerClass, backgroundColor } = getColorVariants(
    isOnDark,
    isHelperNav,
    isNavTransparent,
    sharedStyles,
    cx
  );

  // if nav is not immersive and position is fixed then we want to add placeholder to avoid shift from static to fixed.
  const enablePlaceholderToAvoidShift =
    // biome-ignore lint/suspicious/noDoubleEquals: Automated migration to Biome
    navPosition == NavPosition.FIXED && !immersive;

  return (
    <>
      {enablePlaceholderToAvoidShift && (
        <div className={sharedStyles.placeholder} />
      )}
      {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
      <div
        id='global-nav'
        data-testid='nav-container'
        className={cx({
          [sharedStyles.navContainer]: true,
          [navContainerClass]: true,
        })}
        style={{
          position: getPosition(navPosition),
          top: hideNav === false ? 0 : typography.pxToRem(-133),
          transition: `top 0.5s`,
        }}
      >
        <div
          className={cx(
            sharedStyles.bannerContainer,
            mobileNavOpen && classes.bannerHidden
          )}
        >
          <NavBanner
            userLocation={userLocation}
            setUserLocation={setUserLocation}
            promo={promoCta}
            login={loginCta}
            ctaContent={secondaryCta}
            isOnDark={isOnDark}
            isHelperNav={isHelperNav}
          />
        </div>
        <div
          className={cx({
            [sharedStyles.navBody]: true,
            [classes.selectedItemDark]: useDarkStyles,
            [classes.navBodyMobileSelectedItem]:
              mobileNavOpen && (!!selectedMobileNavItem || locationOpen),
            [backgroundColor]: mobileNavOpen,
          })}
        >
          {' '}
          <div className={sharedStyles.navItem}>
            <div className={sharedStyles.logoContainer}>
              {/** biome-ignore lint/a11y/useSemanticElements: Automated migration to Biome */}
              <div
                role='button' // Add role
                tabIndex={0} // Make focusable
                aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'} // Accessible label
                onClick={() => {
                  trackEvents([
                    {
                      event: 'engagement',
                      id: 'mobile-hamburger-menu',
                    },
                  ]);
                  // close and reset nav state
                  if (mobileNavOpen) {
                    setMobileNavLinksView(false);
                    setSelectedMobileNavItem(null);
                    setLocationOpen(false);
                  }
                  setMobileNavOpen(!mobileNavOpen);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    trackEvents([
                      {
                        event: 'engagement',
                        id: 'mobile-hamburger-menu',
                      },
                    ]);
                    if (mobileNavOpen) {
                      setMobileNavLinksView(false);
                      setSelectedMobileNavItem(null);
                      setLocationOpen(false);
                    }
                    setMobileNavOpen(!mobileNavOpen);
                  }
                }}
                className={classes.hamburgerIconContainer}
                data-testid='hamburgerIcon'
              >
                {mobileNavOpen ? (
                  <Icon
                    aria-label='close menu'
                    name={IconName.CLOSE}
                    color={
                      useDarkStyles
                        ? brandColors.white
                        : brandColors.primaryMain
                    }
                    width={18}
                  />
                ) : (
                  <MenuIcon
                    style={{
                      color: useDarkStyles
                        ? brandColors.white
                        : brandColors.primaryMain,
                    }}
                  />
                )}
              </div>
              {!mobileNavOpen && (
                <Link href='/' noPrefetchOnHover>
                  <TextLogo
                    classname={cx(logoClass, classes.logo)}
                    width='77'
                  />
                </Link>
              )}
            </div>
          </div>
          {selectedMobileNavItem && (
            <>
              <div className={classes.t1ParentPageLabelContainer}>
                <h3> {selectedMobileNavItem.name}</h3>
              </div>
              {/* Matches the width of icon to center item */}
              <div style={{ width: typography.pxToRem(45.5) }} />
            </>
          )}
          {locationOpen && (
            <>
              <div className={classes.t1ParentPageLabelContainer}>
                <h3> {'Update Location'}</h3>
              </div>
              {/* Matches the width of icon to center item */}
              <div style={{ width: typography.pxToRem(45.5) }} />
            </>
          )}
          {!selectedMobileNavItem && !locationOpen && primaryCta && (
            <div className={sharedStyles.navItem}>
              <div className={classes.buttonWrapper}>
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SM}
                  isOnDark={useDarkStyles}
                  label={primaryCta.text as string}
                  href={primaryCta.url as string}
                  gaEvent={{
                    event: 'engagement',
                    id: `button-${primaryCta.text}`,
                  }}
                  noPrefetchOnHover
                />
              </div>
            </div>
          )}
        </div>
        <Fade in={mobileNavOpen}>
          <div
            className={cx(
              classes.mobileNavDropdown,
              useDarkStyles && classes.mobileNavDropdownDark
            )}
          >
            <div
              className={cx(
                classes.mobileNavLeftSide,
                mobileNavLinksView && classes.mobileNavLinksView
              )}
            >
              {categories.map((item, index) => (
                // biome-ignore lint/a11y/useSemanticElements: Automated migration to Biome
                <div
                  role='button' // Add role
                  tabIndex={0} // Make focusable
                  aria-label={`Navigate to ${item.name}`} // Accessible label
                  className={classes.mobileNavItemWrapper}
                  // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                  key={index}
                  onClick={() => {
                    trackEvents([
                      {
                        event: 'engagement',
                        id: item.name,
                        nav_type: 'click',
                      },
                    ]);
                    setSelectedMobileNavItem(
                      selectedMobileNavItem === item ? null : item
                    );
                    setMobileNavLinksView(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      trackEvents([
                        {
                          event: 'engagement',
                          id: item.name,
                          nav_type: 'keyboard',
                        },
                      ]);
                      setSelectedMobileNavItem(
                        selectedMobileNavItem === item ? null : item
                      );
                      setMobileNavLinksView(true);
                    }
                  }}
                >
                  <div
                    className={classes.mobileNavItem}
                    data-testid={`mobileNavItem-${index}`}
                  >
                    <div>{item.name as string}</div>
                    <Icon
                      aria-label='right arrow'
                      name={IconName.CHEVRON_RIGHT}
                      color={
                        useDarkStyles
                          ? brandColors.tintsOffWhite40
                          : brandColors.tintsNeutral20
                      }
                      width={16}
                    />
                  </div>
                </div>
              ))}
              <div className={classes.mobileDropdownInfo}>
                <div>
                  <LocationDisplay
                    isOnDark={useDarkStyles}
                    userLocation={userLocation}
                    setUserLocation={setUserLocation}
                    className={classes.locationDisplay}
                  />
                  <Link
                    className={cx(
                      classes.utilityLink,
                      useDarkStyles && classes.utilityLinkDark
                    )}
                    href={`tel:${PHONE}`}
                  >
                    <Icon
                      aria-label='phone icon'
                      name={IconName.CALL}
                      color={
                        useDarkStyles
                          ? brandColors.tintsChargeBlue60
                          : brandColors.mediumBlue
                      }
                    />
                    <div>{PHONE}</div>
                  </Link>
                  <Link
                    className={cx(
                      classes.utilityLink,
                      useDarkStyles && classes.utilityLinkDark
                    )}
                    href={getLoginUrlByEnv()}
                    data-testid='global-nav-login'
                  >
                    <Icon
                      aria-label='login icon'
                      name={IconName.ACCOUNT}
                      color={
                        useDarkStyles
                          ? brandColors.tintsChargeBlue60
                          : brandColors.mediumBlue
                      }
                    />
                    <div>Login</div>
                  </Link>
                </div>
              </div>
            </div>
            <div className={classes.mobileNavRightSide}>
              {selectedMobileNavItem && (
                <div className={sharedStyles.dropdownContainer}>
                  <Dropdown
                    isOnDark={useDarkStyles}
                    dropdown={selectedMobileNavItem}
                    closeMobileDropdown={() => {
                      mobileDropdownCloseDebounce();
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </Fade>
      </div>
    </>
  );
};

export default MobileNav;
